import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { UserClaims } from '@okta/okta-auth-js';
import Sidebar from './components/Sidebar';
import WelcomePage from './components/WelcomePage';
import NewProjectModal from './components/NewProjectModal';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import {
  initializeThread,
  selectSelectedCount,
  selectOpenDetails,
  selectThreadError,
  selectThreadStatus,
  selectThread,
  selectCurrentLastEditTime,
  selectCurrentProjectType,
  selectCurrentCatalogMode,
  resetThread,
  selectThreadAppState,
  selectLockedProject,
  clearLockedProject,
  loadAvailableProjects,
} from './features/thread/threadSlice';
import ListProducts from './features/products/ListProducts';
import SimilarityMatrix from './features/similarity/SimilarityMatrix';
import ForecastV2 from './features/forecast/ForecastV2';
import OptimizationResultView from './components/OptimizationResultView';
import MerchandisingPlanPage from './components/MerchandisingPlanPage';
import DetailsModal from './components/DetailsModal';
import StateModal from './components/StateModal';
import StorageModal from './components/StorageModal';
import ProjectTypeSelectorModal from './components/ProjectTypeSelectorModal';
import ProfileModal from './components/ProfileModal';
import FeedbackModal from './components/FeedbackModal';
import { WeborderExportModal } from './components/WeborderExportModal';
import Modal from './components/Modal';
import { getOptimizationDraft, hasSucceededRun } from './features/thread/optimizationDraft';
import { executeSophiaAction, selectProjectName } from './features/thread/threadSlice';
import { performAction } from './api/sophia';
import { useAuth } from './auth/useAuth';
import { useProjectLock } from './hooks/useProjectLock';
import { AlertModal } from './components/AlertModal';
import { confirmLeaveUnsavedMp } from './hooks/useConfirmLeaveUnsavedMp';
import { isAdminUser } from './auth/admin';
import AdminMappingsDashboard from './components/AdminMappingsDashboard';
import AdminForecastDashboard from './components/AdminForecastDashboard';
import Logo from './assets/logo.png';
import { ProjectType } from './api/sophia';
import { getCollectionFromCatalogMode } from './types/catalogMode';
import { getCatalogModeLabel, type CatalogMode } from './types/catalogMode';
import * as XLSX from 'xlsx';

const SIDEBAR_WIDTH_CLASS = 'pr-28';

type ConsultationTabKey = 'list' | 'compare' | 'forecast';
type OptimizationTabKey = 'result' | 'merchandising-plan' | 'confronti';
type TabKey = ConsultationTabKey | OptimizationTabKey;
type AdminTabKey = 'mappings' | 'forecast';

const LOCAL_STORAGE_TAB_KEY = 'app-active-tab';
const rawMappingsBaseUrl = ((import.meta.env.VITE_MAPPINGS_API_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:8801').replace('://0.0.0.0', '://localhost');
const mappingsBaseUrl = rawMappingsBaseUrl;

const DONNA_TEMPLATE_URL = new URL('../excel_format_out/TOT_DONNA.xlsx', import.meta.url).href;
const UOMO_TEMPLATE_URL = new URL('../excel_format_out/TOT_UOMO.xlsx', import.meta.url).href;
const BASE_BLOCK_START_COL = 12; // L
const BLOCK_WIDTH_COLS = 19;
const MAX_TEMPLATE_SIZE_COLS = 16;

// Frontend override of the backend i_arr slot mapping returned by get-taglie-info,
// so slot assignment can be corrected without a backend deploy. Keyed by the
// normalized 2-digit cod_tipo_taglia. cod 06 is pinned to slot 1 here.
const I_ARR_OVERRIDES: Record<string, number> = { '06': 1 };

const normalizeSizeKey = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    const raw = Number.isInteger(value) ? String(value) : String(value);
    return raw.replace(',', '.').trim().toUpperCase();
  }
  return String(value).replace(',', '.').trim().toUpperCase();
};

// --- size-scale taxonomy & ordering -----------------------------------------
// Size labels in dwh_anag_taglia_old span several scales (numeric incl. half
// sizes and "12+", letters XXS..6XL, baby months "12M", one-size "OS", plus the
// "-" spacer code 41 uses). Family ranks keep different scales from interleaving;
// within a family the numeric/ordinal value decides order.
const SF = { OneSize: 0, Numeric: 1, Month: 2, Letter: 3, Roman: 5, Other: 8, Drop: 99 } as const;

const LETTER_LADDER: Record<string, number> = {
  XXXS: -30, XXS: -20, XS: -10, S: 0, M: 10, L: 20, XL: 30,
  XX: 40, XXL: 40, LL: 40,
  XXX: 50, '3X': 50, '3XL': 50, L3: 50, // same physical size, different notations
  '4X': 60, '4XL': 60,
  '5X': 70, '5XL': 70,
  '6X': 80, '6XL': 80,
};
const ROMAN_LADDER: Record<string, number> = { I: 1, II: 2, III: 3, IV: 4, V: 5 };
const ONE_SIZE_TOKENS = new Set(['OS', 'TU', 'U', 'UNICA']);
const DROP_TOKENS = new Set(['', '-', '--', '.', '.N']);
const MONTH_RE = /^(\d+(?:\.\d+)?)M$/;

// "36½"→36.5, "12+"→12.1, "38,5"→38.5, "44"→44 ; null when not numeric
const parseNumericSize = (token: string): number | null => {
  let s = token;
  let frac = 0;
  let plus = 0;
  if (s.endsWith('+')) { plus = 0.1; s = s.slice(0, -1); }
  for (const [glyph, value] of [['½', 0.5], ['¼', 0.25], ['¾', 0.75]] as const) {
    if (s.endsWith(glyph)) { frac = value; s = s.slice(0, -1); break; }
  }
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n + frac + plus : null;
};

// [familyRank, value, normalizedToken]
type SizeKey = [number, number, string];
const sizeSortKey = (raw: unknown): SizeKey => {
  const t = normalizeSizeKey(raw);
  if (DROP_TOKENS.has(t)) return [SF.Drop, 0, t];
  if (ONE_SIZE_TOKENS.has(t)) return [SF.OneSize, 0, t];
  const month = MONTH_RE.exec(t);
  if (month) return [SF.Month, parseFloat(month[1]), t];
  const num = parseNumericSize(t);
  if (num !== null) return [SF.Numeric, num, t];
  if (t in LETTER_LADDER) return [SF.Letter, LETTER_LADDER[t], t];
  if (t in ROMAN_LADDER) return [SF.Roman, ROMAN_LADDER[t], t];
  const sz = /^SZ(\d+)$/.exec(t);
  if (sz) return [SF.Other, parseFloat(sz[1]), t];
  return [SF.Other, 0, t];
};

const compareSizeKeys = (a: SizeKey, b: SizeKey): number =>
  a[0] - b[0] || a[1] - b[1] || (a[2] < b[2] ? -1 : a[2] > b[2] ? 1 : 0);

const compareSizes = (a: unknown, b: unknown): number =>
  compareSizeKeys(sizeSortKey(a), sizeSortKey(b));

// Collapses equivalent sizes (XXX≡3XL, 4X≡4XL …) onto one column: recognised
// families are identified by their value, unknown 'Other' tokens by string.
const sizeIdentity = (raw: unknown): string => {
  const [fam, val, tok] = sizeSortKey(raw);
  return fam === SF.Numeric || fam === SF.Month || fam === SF.Letter
    || fam === SF.Roman || fam === SF.OneSize
    ? `${fam}:${val}`
    : `${fam}:${tok}`;
};

type MergedColumn = { identity: string; aliases: string[]; label: string };

// One ordered, alias-collapsed size grid for all codes that share an i_arr slot.
const buildSlotColumns = (codes: ResolvedTaglieInfoEntry[]): MergedColumn[] => {
  const byIdentity = new Map<string, { key: SizeKey; aliases: Set<string> }>();
  [...codes]
    .sort((a, b) => (a.cod_tipo_taglia < b.cod_tipo_taglia ? -1 : 1)) // deterministic first-seen
    .forEach((code) => {
      code.griglia_taglie.forEach((token) => {
        const key = sizeSortKey(token);
        if (key[0] === SF.Drop) return;
        const identity = sizeIdentity(token);
        const existing = byIdentity.get(identity);
        if (existing) existing.aliases.add(key[2]);
        else byIdentity.set(identity, { key, aliases: new Set([key[2]]) });
      });
    });
  return [...byIdentity.entries()]
    .sort((a, b) => compareSizeKeys(a[1].key, b[1].key))
    .map(([identity, entry]) => {
      const aliases = [...entry.aliases];
      return { identity, aliases, label: aliases[0] };
    });
};

const sanitizeSheetName = (value: string): string =>
  String(value || 'Foglio')
    .replace(/[\\/*?:\[\]]/g, '_')
    .slice(0, 31) || 'Foglio';

const getLayoutRowsByIndex = (
  ws: XLSX.WorkSheet,
  headerRow: number,
  blockStartCol: number,
): Record<number, number> => {
  const rows: Record<number, number> = {};
  for (let row = 1; row < headerRow; row += 1) {
    const raw = ws[XLSX.utils.encode_cell({ r: row - 1, c: blockStartCol - 1 })]?.v;
    const value = Number(raw);
    if (Number.isFinite(value) && value > 0) {
      rows[value] = row;
    }
  }
  return rows;
};

const clearSheetValues = (ws: XLSX.WorkSheet, fromRow: number, toRow: number, toCol: number) => {
  for (let row = fromRow; row <= toRow; row += 1) {
    for (let col = 1; col <= toCol; col += 1) {
      const cellRef = XLSX.utils.encode_cell({ r: row - 1, c: col - 1 });
      const cell = ws[cellRef];
      if (!cell) continue;
      delete cell.f;
      delete cell.w;
      cell.v = undefined;
      cell.t = 'z';
    }
  }
};

const setCellValue = (
  ws: XLSX.WorkSheet,
  row: number,
  col: number,
  value: string | number | null | undefined,
) => {
  const ref = XLSX.utils.encode_cell({ r: row - 1, c: col - 1 });
  const existingCell = ws[ref] ? { ...ws[ref] } : {};
  delete existingCell.f;
  delete existingCell.w;
  if (value === null || value === undefined || value === '') {
    ws[ref] = {
      ...existingCell,
      t: 'z',
      v: undefined,
    };
    return;
  }

  ws[ref] = {
    ...existingCell,
    t: typeof value === 'number' ? 'n' : 's',
    v: value,
  };
};

const copyRowTemplate = (
  ws: XLSX.WorkSheet,
  sourceRow: number,
  targetRow: number,
  toCol: number,
) => {
  for (let col = 1; col <= toCol; col += 1) {
    const srcRef = XLSX.utils.encode_cell({ r: sourceRow - 1, c: col - 1 });
    const dstRef = XLSX.utils.encode_cell({ r: targetRow - 1, c: col - 1 });
    const srcCell = ws[srcRef];
    if (srcCell) {
      ws[dstRef] = JSON.parse(JSON.stringify(srcCell));
    } else {
      delete ws[dstRef];
    }
  }
};

const flattenTaglieDistribution = (
  value: unknown,
): Record<string, number> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const flattened: Record<string, number> = {};
  Object.values(value as Record<string, unknown>).forEach((group) => {
    if (!group || typeof group !== 'object' || Array.isArray(group)) return;
    Object.entries(group as Record<string, unknown>).forEach(([size, qty]) => {
      const normalizedSize = normalizeSizeKey(size);
      const quantity = Number(qty);
      if (!normalizedSize || !Number.isFinite(quantity) || quantity === 0) return;
      flattened[normalizedSize] = (flattened[normalizedSize] || 0) + quantity;
    });
  });
  return flattened;
};

const mergeFlatTaglieMaps = (
  target: Record<string, number>,
  source: Record<string, number>,
) => {
  Object.entries(source).forEach(([size, qty]) => {
    target[size] = (target[size] || 0) + qty;
  });
};

const pickPreferredString = (...values: unknown[]): string => {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
};

const pickPreferredNumber = (...values: unknown[]): number | '' => {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return '';
};

const findHeaderRow = (ws: XLSX.WorkSheet): number => {
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1');
  for (let row = range.s.r; row <= range.e.r; row += 1) {
    const value = ws[XLSX.utils.encode_cell({ r: row, c: 0 })]?.v;
    if (String(value || '').trim().toUpperCase() === '#PO') {
      return row + 1;
    }
  }
  return 8;
};

const isAreaShop = (shopId: string, shopMapping?: Record<string, string>): boolean => {
  const cleanId = shopId.replace(/\|$/, '').trim().toLowerCase();
  const mapped = String(shopMapping?.[shopId.replace(/\|$/, '').trim()] || '').trim().toLowerCase();
  return cleanId === 'area' || mapped === 'area';
};

const splitSkuCode = (rawCode: string): { season: string; model: string; color: string } => {
  const code = String(rawCode || '').trim().toUpperCase();
  if (!code) return { season: '', model: '', color: '' };

  const season = code.slice(0, 3);
  const rest = code.slice(3);
  const lastCIndex = rest.lastIndexOf('C');

  if (lastCIndex >= 0) {
    return {
      season,
      model: rest.slice(0, lastCIndex),
      color: rest.slice(lastCIndex),
    };
  }

  return { season, model: rest, color: '' };
};

const parseWeborderTags = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter((item) => item.length > 0);
  }
  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter((item) => item.length > 0);
    }
  } catch {
    // fall back to comma-separated values
  }

  return trimmed.split(',').map((item) => item.trim()).filter((item) => item.length > 0);
};

const deriveResortSpringValue = (tags: unknown): 'S' | 'R' | '' => {
  const normalizedTags = parseWeborderTags(tags).map((tag) => tag.toLowerCase());
  if (normalizedTags.some((tag) => tag.includes('spring'))) return 'S';
  if (normalizedTags.some((tag) => tag.includes('resort'))) return 'R';
  return '';
};

const findFieldDefaultFromSchema = (
  schema: Record<string, Record<string, Array<{ name: string; default?: string | number }>>> | null | undefined,
  fieldNames: string[],
): string => {
  if (!schema) return '';
  for (const shopSchema of Object.values(schema)) {
    for (const fields of Object.values(shopSchema || {})) {
      for (const field of fields || []) {
        if (fieldNames.includes(field.name) && field.default !== undefined && field.default !== null) {
          return String(field.default);
        }
      }
    }
  }
  return '';
};

const findFieldDefaultForConsegna = (
  schema: Record<string, Record<string, Array<{ name: string; default?: string | number }>>> | null | undefined,
  consegnaName: string,
  fieldNames: string[],
): string => {
  if (!schema) return '';
  for (const shopSchema of Object.values(schema)) {
    const consegnaFields = shopSchema?.[consegnaName];
    if (!consegnaFields) continue;
    for (const field of consegnaFields) {
      if (fieldNames.includes(field.name) && field.default !== undefined && field.default !== null) {
        return String(field.default);
      }
    }
  }
  return findFieldDefaultFromSchema(schema, fieldNames);
};

type ExcelExportConsegnaFormValue = {
  tipologiaOrdine: string;
  codiceConsegna: string;
};

type TaglieInfoEntry = {
  cod_tipo_taglia?: string;
  griglia_taglie?: string[];
  i_arr_taglia?: number | null;
  i_arr?: number | null;
  error?: string | null;
};

type ResolvedTaglieInfoEntry = {
  cod_tipo_taglia: string;
  griglia_taglie: string[];
  i_arr_taglia: number;
  i_arr: number;
};

type AggregatedExportRow = {
  skuName: string;
  tf: string;
  codTipoTaglia: string;
  season: string;
  model: string;
  color: string;
  desModello: string;
  desColore: string;
  whsPrice: number | '';
  retailPrice: number | '';
  taglieTotals: Record<string, number>;
  resortSpring: 'S' | 'R' | '';
};

type ExportSection = {
  key: string;
  label: string;
  rows: AggregatedExportRow[];
};

// Una consegna con le sue sezioni per negozio. Uno sheet e' fatto di uno o piu' gruppi:
// gli sheet per consegna ne hanno uno, "Totale consegne" li impila tutti.
type ExportGroup = {
  consegna?: string;
  sections: ExportSection[];
};

const getShopDisplayName = (shopId: string, shopMapping?: Record<string, string>): string => {
  const raw = String(shopId || '');
  // SPJ-891: negozi aggregati (id "EC1|,|EC2") → mappa ogni componente al suo nome
  // e uniscili (es. "ECOMMERCE EUROPA, ECOMMERCE USA") invece di mostrare l'id grezzo.
  if (raw.includes('|,|')) {
    const names = raw
      .split('|,|')
      .map((part) => part.replace(/\|/g, '').trim())
      .filter((part) => part.length > 0)
      .map((part) => (shopMapping?.[part] || part).trim());
    if (names.length > 0) return names.join(', ');
  }
  const cleanId = raw.replace(/\|$/, '').trim();
  return (shopMapping?.[cleanId] || cleanId || 'SHOP').trim();
};

const normalizeCodTipoTaglia = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const raw = String(value).trim();
  if (!raw) {
    return '';
  }

  if (/^\d+$/.test(raw)) {
    return String(Number(raw)).padStart(2, '0');
  }

  return raw;
};

const extractCodTipoTaglia = (sku: any): string => {
  const directCandidates = [
    sku?.cod_tipo_taglia,
    sku?.codTipoTaglia,
    sku?.metrics?.cod_tipo_taglia,
    sku?.metrics?.codTipoTaglia,
    sku?.metrics?.taglie_distribution?.cod_tipo_taglia,
    sku?.taglie_distribution?.cod_tipo_taglia,
  ];
  const direct = normalizeCodTipoTaglia(pickPreferredString(...directCandidates));
  if (direct) return direct;

  const taglieDistribution = sku?.metrics?.taglie_distribution;
  if (taglieDistribution && typeof taglieDistribution === 'object' && !Array.isArray(taglieDistribution)) {
    const outerKeys = Object.keys(taglieDistribution as Record<string, unknown>)
      .map((key) => normalizeCodTipoTaglia(key))
      .filter((key) => key.length > 0 && key !== 'cod_tipo_taglia');
    if (outerKeys.length === 1) {
      return outerKeys[0];
    }
  }

  if (taglieDistribution && typeof taglieDistribution === 'object' && !Array.isArray(taglieDistribution)) {
    for (const group of Object.values(taglieDistribution as Record<string, unknown>)) {
      if (!group || typeof group !== 'object' || Array.isArray(group)) continue;
      const nested = normalizeCodTipoTaglia(pickPreferredString(
        (group as Record<string, unknown>).cod_tipo_taglia,
        (group as Record<string, unknown>).codTipoTaglia,
      ));
      if (nested) return nested;
    }
  }

  return '';
};

const buildAggregatedExportRows = (
  result: any,
  completeExportInfo: Map<string, { codModello: string; codColore: string; desModello: string; desColore: string }>,
) => {
  const aggregatedByKey = new Map<string, AggregatedExportRow>();
  const aggregatedByShop = new Map<string, Map<string, AggregatedExportRow>>();
  const aggregatedByConsegnaByShop = new Map<string, Map<string, Map<string, AggregatedExportRow>>>();
  const skuCodTipoBySku = new Map<string, string>();
  const shopEntries = Object.entries((result?.metrics_per_consegna_per_shop || {}) as Record<string, any[]>)
    .filter(([shopId]) => !isAreaShop(shopId, result?.shop_mapping));

  shopEntries.forEach(([shopId, shopConsegnas]) => {
    if (!aggregatedByShop.has(shopId)) {
      aggregatedByShop.set(shopId, new Map<string, AggregatedExportRow>());
    }

    (shopConsegnas || [])
      .filter((consegna: any) => String(consegna?.name || '').trim().toLowerCase() !== 'all')
      .forEach((consegna: any) => {
        const consegnaName = String(consegna?.name || '').trim();
        if (!consegnaName) return;

        const byShopForConsegna = aggregatedByConsegnaByShop.get(consegnaName) || new Map<string, Map<string, AggregatedExportRow>>();
        if (!byShopForConsegna.has(shopId)) {
          byShopForConsegna.set(shopId, new Map<string, AggregatedExportRow>());
          aggregatedByConsegnaByShop.set(consegnaName, byShopForConsegna);
        }

        (consegna?.tf || []).forEach((tf: any) => {
          const tfName = String(tf?.name || '').trim();
          (tf?.sku || []).forEach((sku: any) => {
            const metrics = sku?.metrics || {};
            const skuName = String(sku?.name || metrics?.model || '').trim();
            if (!skuName) return;
            const taglieTotals = flattenTaglieDistribution(metrics?.taglie_distribution);
            const totalQuantity = Object.values(taglieTotals).reduce((sum, value) => sum + value, 0);

            const codTipoTaglia = extractCodTipoTaglia(sku);
            if (Number(codTipoTaglia) === 0 && totalQuantity === 0) {
              return;
            }
            if (!codTipoTaglia) {
              throw new Error(`Cod tipo taglia mancante per SKU ${skuName}`);
            }

            const existingCodTipo = skuCodTipoBySku.get(skuName);
            if (existingCodTipo && existingCodTipo !== codTipoTaglia) {
              throw new Error(`SKU ${skuName} ha cod_tipo_taglia multipli (${existingCodTipo}, ${codTipoTaglia})`);
            }
            skuCodTipoBySku.set(skuName, codTipoTaglia);

            const parts = splitSkuCode(skuName);
            const missingInfo = completeExportInfo.get(skuName);
            const aggregateKey = skuName;

            const upsertRow = (targetMap: Map<string, AggregatedExportRow>) => {
              const current = targetMap.get(aggregateKey);
              if (current) {
                mergeFlatTaglieMaps(current.taglieTotals, taglieTotals);
                current.tf = pickPreferredString(current.tf, tfName);
                current.desModello = pickPreferredString(current.desModello, missingInfo?.desModello);
                current.desColore = pickPreferredString(current.desColore, missingInfo?.desColore);
                current.whsPrice = pickPreferredNumber(current.whsPrice, metrics?.mean_weight, metrics?.whs_price);
                current.retailPrice = pickPreferredNumber(current.retailPrice, metrics?.mean_price, metrics?.retail_price);
                current.resortSpring = current.resortSpring || deriveResortSpringValue(metrics?.weborder_tags);
                return;
              }

              targetMap.set(aggregateKey, {
                skuName,
                tf: tfName,
                codTipoTaglia,
                season: parts.season,
                model: pickPreferredString(missingInfo?.codModello, parts.model, skuName),
                color: pickPreferredString(missingInfo?.codColore, parts.color),
                desModello: pickPreferredString(missingInfo?.desModello),
                desColore: pickPreferredString(missingInfo?.desColore),
                whsPrice: pickPreferredNumber(metrics?.mean_weight, metrics?.whs_price),
                retailPrice: pickPreferredNumber(metrics?.mean_price, metrics?.retail_price),
                taglieTotals: { ...taglieTotals },
                resortSpring: deriveResortSpringValue(metrics?.weborder_tags),
              });
            };

            upsertRow(aggregatedByKey);

            const totalPerShop = aggregatedByShop.get(shopId) || new Map<string, AggregatedExportRow>();
            upsertRow(totalPerShop);
            aggregatedByShop.set(shopId, totalPerShop);

            const perShop = byShopForConsegna.get(shopId) || new Map<string, AggregatedExportRow>();
            upsertRow(perShop);
            byShopForConsegna.set(shopId, perShop);
          });
        });
      });
  });

  const sortRows = (rows: AggregatedExportRow[]) =>
    [...rows].sort((a, b) =>
      a.tf.localeCompare(b.tf, 'it', { numeric: true }) ||
      a.skuName.localeCompare(b.skuName, 'it', { numeric: true }),
    );

  return {
    totalRows: sortRows(Array.from(aggregatedByKey.values())),
    totalSections: Array.from(aggregatedByShop.entries())
      .map(([shopId, rows]) => ({
        key: shopId,
        label: getShopDisplayName(shopId, result?.shop_mapping),
        rows: sortRows(Array.from(rows.values())),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it', { numeric: true })),
    sectionsByConsegna: new Map(
      Array.from(aggregatedByConsegnaByShop.entries()).map(([consegna, shops]) => [
        consegna,
        Array.from(shops.entries())
          .map(([shopId, rows]) => ({
            key: shopId,
            label: getShopDisplayName(shopId, result?.shop_mapping),
            rows: sortRows(Array.from(rows.values())),
          }))
          .sort((a, b) => a.label.localeCompare(b.label, 'it', { numeric: true })),
      ]),
    ),
    codTipoTagliaList: Array.from(new Set(Array.from(skuCodTipoBySku.values()))).sort((a, b) => a.localeCompare(b, 'it', { numeric: true })),
  };
};

type MerchandisingPlanEntryMode = 'new-optimization' | 'from-result';

type OptimizationResultRestoreContext = {
  runId: number | null;
  selectedShopId: string | null;
  selectedResultTab: number;
};

export default function App() {
  const { authState, oktaAuth } = useAuth();
  const dispatch = useAppDispatch();
  const selectedCount = useAppSelector(selectSelectedCount);
  const projectName = useAppSelector(selectProjectName);
  const detailsSku = useAppSelector(selectOpenDetails);
  const threadError = useAppSelector(selectThreadError);
  const threadStatus = useAppSelector(selectThreadStatus);
  const thread = useAppSelector(selectThread);
  const lastEditTime = useAppSelector(selectCurrentLastEditTime);
  const appState = useAppSelector(selectThreadAppState);
  const projectType = useAppSelector(selectCurrentProjectType);
  const currentCatalogMode = useAppSelector(selectCurrentCatalogMode);

  // Initialize activeTab based on project type
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    try {
      if (projectType === 'optimization') {
        // For optimization projects, always show 'result' tab
        return 'result';
      } else {
        const savedTab = localStorage.getItem(LOCAL_STORAGE_TAB_KEY) as TabKey;
        return savedTab && ['list', 'compare', 'forecast'].includes(savedTab) ? savedTab as ConsultationTabKey : 'list';
      }
    } catch {
      return projectType === 'optimization' ? 'result' : 'list';
    }
  });
  const [optimizationView, setOptimizationView] = useState<'result' | 'merchandising-plan'>('result');
  const [merchandisingPlanEntryMode, setMerchandisingPlanEntryMode] = useState<MerchandisingPlanEntryMode>('new-optimization');
  // SPJ-1366: la MP riprende la configurazione in corso invece di crearne una nuova dalla run.
  const [resumeOptimizationDraft, setResumeOptimizationDraft] = useState(false);
  // "Ottimizza" premuto mentre c'e' gia' una bozza: si chiede cosa fare.
  const [pendingOptimizeContext, setPendingOptimizeContext] = useState<Partial<OptimizationResultRestoreContext> | null>(null);
  const [optimizationResultRestoreContext, setOptimizationResultRestoreContext] = useState<OptimizationResultRestoreContext>({
    runId: null,
    selectedShopId: null,
    selectedResultTab: 0,
  });
  const [shouldRestoreOptimizationResultContext, setShouldRestoreOptimizationResultContext] = useState(false);
  const [isStateOpen, setStateOpen] = useState(false);
  const [isStorageOpen, setStorageOpen] = useState(false);
  const [isProjectTypeSelectorOpen, setProjectTypeSelectorOpen] = useState(false);
  const [isNewProjectOpen, setNewProjectOpen] = useState(false);
  const [selectedProjectType, setSelectedProjectType] = useState<ProjectType | null>(null);
  const [pendingOptimizationCatalogMode, setPendingOptimizationCatalogMode] = useState<CatalogMode | ''>('');
  // SPJ-1372: flag incognito scelto nello step famiglia stagionale, propagato alla creazione.
  const [pendingOptimizationIncognito, setPendingOptimizationIncognito] = useState(false);
  const [isCreatingOptimizationProject, setIsCreatingOptimizationProject] = useState(false);
  const [isProfileOpen, setProfileOpen] = useState(false);
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);
  const [isWeborderExportModalOpen, setWeborderExportModalOpen] = useState(false);
  const [isExcelExportModalOpen, setExcelExportModalOpen] = useState(false);
  const [excelExportConsegne, setExcelExportConsegne] = useState<string[]>([]);
  const [excelExportForm, setExcelExportForm] = useState<Record<string, ExcelExportConsegnaFormValue>>({});
  const [excelExportSplitAggregatedShops, setExcelExportSplitAggregatedShops] = useState(false);
  const [excelExportPerSkuGrid, setExcelExportPerSkuGrid] = useState(false);
  // SPJ-891: raggruppamenti scelti dall'utente per lo split "dividi per negozi".
  // Mappa id-negozio -> etichetta gruppo; negozi con la stessa etichetta vengono
  // esportati come un unico blocco (budget = somma). Vuoto = split completo (default).
  const [excelExportShopGroups, setExcelExportShopGroups] = useState<Record<string, string>>({});
  const [excelExportAggregatedShops, setExcelExportAggregatedShops] = useState<Array<{ sourceId: string; members: Array<{ id: string; label: string }> }>>([]);
  const [isExcelExportLoadingDefaults, setExcelExportLoadingDefaults] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [lastKnownThreadId, setLastKnownThreadId] = useState<string | null>(null);
  const [user, setUser] = useState<UserClaims | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminDashboardOpen, setIsAdminDashboardOpen] = useState(false);
  const [activeAdminTab, setActiveAdminTab] = useState<AdminTabKey>('mappings');
  const [hasAdminNotification, setHasAdminNotification] = useState(false);
  const [adminNotificationItems, setAdminNotificationItems] = useState<string[]>([]);

  const isTruthyFlag = useCallback((value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return false;
  }, []);

  const hasTipoFilatoAlertRows = useCallback(
    (value: unknown): boolean => {
      if (!Array.isArray(value)) return false;
      return value.some((entry) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
        const row = entry as Record<string, unknown>;
        return isTruthyFlag(row.is_new) && isTruthyFlag(row.is_default);
      });
    },
    [isTruthyFlag],
  );
  const countTipoFilatoAlertRows = useCallback(
    (value: unknown): number => {
      if (!Array.isArray(value)) return 0;
      return value.filter((entry) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
        const row = entry as Record<string, unknown>;
        return isTruthyFlag(row.is_new) && isTruthyFlag(row.is_default);
      }).length;
    },
    [isTruthyFlag],
  );
  const toObjectRows = useCallback((value: unknown): Record<string, unknown>[] => {
    if (!Array.isArray(value)) return [];
    return value.filter(
      (entry): entry is Record<string, unknown> =>
        Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry),
    );
  }, []);
  const extractMissingRows = useCallback(
    (payload: unknown, actionKey: string, missingKey: string): Record<string, unknown>[] => {
      const root = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
      const dataLayer = root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : null;
      const source = dataLayer ?? root;
      if (Array.isArray(source[missingKey])) {
        return toObjectRows(source[missingKey]);
      }
      const actionContainer = source[actionKey];
      if (actionContainer && typeof actionContainer === 'object') {
        return toObjectRows((actionContainer as Record<string, unknown>)[missingKey]);
      }
      return [];
    },
    [toObjectRows],
  );

  useEffect(() => {
    let isMounted = true;
    if (!authState?.isAuthenticated) {
      setUser(null);
      return () => {
        isMounted = false;
      };
    }
    if (oktaAuth && 'getUser' in oktaAuth && typeof oktaAuth.getUser === 'function') {
      oktaAuth
        .getUser()
        .then((info: UserClaims) => {
          if (isMounted) setUser(info);
        })
        .catch(() => {
          if (isMounted) setUser(null);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [authState, oktaAuth]);

  const localAdmin = useMemo(() => isAdminUser(user), [user]);

  useEffect(() => {
    let isMounted = true;

    if (!authState?.isAuthenticated) {
      setIsAdmin(false);
      return () => {
        isMounted = false;
      };
    }

    if (localAdmin) {
      setIsAdmin(true);
    }
    else {
      setIsAdmin(false);
    }

    return () => {
      isMounted = false;
    };
  }, [authState?.isAuthenticated, localAdmin]);

  useEffect(() => {
    if (!isAdmin && isAdminDashboardOpen) {
      setIsAdminDashboardOpen(false);
      setActiveAdminTab('mappings');
    }
  }, [isAdmin, isAdminDashboardOpen]);

  useEffect(() => {
    const syncAdminNotification = async () => {
      if (!isAdmin || !thread.threadId) {
        setHasAdminNotification(false);
        setAdminNotificationItems([]);
        return;
      }
      const currentMessageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string | undefined;
      if (!currentMessageId) {
        setHasAdminNotification(false);
        setAdminNotificationItems([]);
        return;
      }

      try {
        let tipoFilatoRows: unknown = [];
        let merchMissing: Record<string, unknown>[] = [];
        let negoziMissing: Record<string, unknown>[] = [];

        try {
          const collection = getCollectionFromCatalogMode(currentCatalogMode);
          const modeSuffix = collection ? `?collection=${encodeURIComponent(collection)}` : '';
          const [tipoFilatoResp, merchandisingResp, negoziResp] = await Promise.all([
            fetch(`${mappingsBaseUrl}/mappings/tipo-filato${modeSuffix}`),
            fetch(`${mappingsBaseUrl}/mappings/merchandising-plan${modeSuffix}`),
            fetch(`${mappingsBaseUrl}/mappings/negozi${modeSuffix}`),
          ]);
          if (!tipoFilatoResp.ok || !merchandisingResp.ok || !negoziResp.ok) {
            throw new Error('Mappings API not available');
          }
          const [tipoFilatoResult, merchandisingResult, negoziResult] = await Promise.all([
            tipoFilatoResp.json(),
            merchandisingResp.json(),
            negoziResp.json(),
          ]);
          tipoFilatoRows = Array.isArray(tipoFilatoResult) ? tipoFilatoResult : ((tipoFilatoResult as Record<string, unknown>)?.entries ?? []);
          merchMissing = extractMissingRows(
            merchandisingResult,
            'admin-mappings-merchandising-plan',
            'missing_tipo_filato_ds',
          );
          negoziMissing = extractMissingRows(
            negoziResult,
            'admin-mappings-negozi',
            'missing_shops',
          );
        } catch {
          try {
            const [tipoFilatoResult, merchandisingResult, negoziResult] = await Promise.all([
              performAction({
                threadId: thread.threadId,
                messageId: currentMessageId,
                actionName: 'admin-get-mappings-tipo-filato',
                params: {},
              }),
              performAction({
                threadId: thread.threadId,
                messageId: currentMessageId,
                actionName: 'admin-get-mappings-merchandising-plan',
                params: {},
              }),
              performAction({
                threadId: thread.threadId,
                messageId: currentMessageId,
                actionName: 'admin-get-mappings-negozi',
                params: {},
              }),
            ]);
            tipoFilatoRows = Array.isArray(tipoFilatoResult)
              ? tipoFilatoResult
              : ((tipoFilatoResult as Record<string, unknown>)?.entries ?? []);
            merchMissing = extractMissingRows(
              merchandisingResult,
              'admin-mappings-merchandising-plan',
              'missing_tipo_filato_ds',
            );
            negoziMissing = extractMissingRows(
              negoziResult,
              'admin-mappings-negozi',
              'missing_shops',
            );
          } catch {
            const legacyResult = (await performAction({
              threadId: thread.threadId,
              messageId: currentMessageId,
              actionName: 'admin-get-mappings',
              params: {},
            })) as Record<string, unknown>;
            const rawMappings = (legacyResult?.['admin-mappings'] ?? {}) as Record<string, unknown>;
            tipoFilatoRows = rawMappings['tipo-filato'];
            merchMissing = toObjectRows(
              ((rawMappings['merchandising-plan'] as Record<string, unknown> | undefined)?.missing_tipo_filato_ds)
            );
            negoziMissing = toObjectRows(
              ((rawMappings.negozi as Record<string, unknown> | undefined)?.missing_shops)
            );
          }
        }

        const hasMissing = merchMissing.length > 0 || negoziMissing.length > 0;
        const tipoFilatoCount = countTipoFilatoAlertRows(tipoFilatoRows);
        const reasons: string[] = [];
        if (tipoFilatoCount > 0) reasons.push(`Tipo Filato: ${tipoFilatoCount} righe problematiche`);
        if (merchMissing.length > 0) reasons.push(`Merchandising Plan: ${merchMissing.length} elementi mancanti`);
        if (negoziMissing.length > 0) reasons.push(`Negozi: ${negoziMissing.length} shop mancanti`);
        setHasAdminNotification(hasTipoFilatoAlertRows(tipoFilatoRows) || hasMissing);
        setAdminNotificationItems(reasons);
      } catch {
        setHasAdminNotification(false);
        setAdminNotificationItems([]);
      }
    };

    void syncAdminNotification();
  }, [countTipoFilatoAlertRows, currentCatalogMode, extractMissingRows, hasTipoFilatoAlertRows, isAdmin, thread.threadData, thread.threadId, toObjectRows]);

  // Custom setActiveTab that saves to localStorage
  const setActiveTabWithStorage = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    try {
      localStorage.setItem(LOCAL_STORAGE_TAB_KEY, tab);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  useEffect(() => {
    dispatch(initializeThread());
  }, [dispatch]);

  // Check if we have a valid thread loaded
  const hasActiveProject = thread.threadId && thread.threadData;

  // Get optimization ID for exports
  const currentSelectedRunId = useMemo<number | null>(() => {
    const optimizationId = appState?.['optim-optimization-id'];
    return optimizationId ? Number(optimizationId) : null;
  }, [appState]);

  // Can export if we have a valid optimization project with a run ID
  const canExport = projectType === 'optimization' && currentSelectedRunId !== null;

  // Set active tab when project type changes
  useEffect(() => {
    if (projectType === 'optimization') {
      setActiveTab('result');
    }
  }, [projectType]);

  // Track current thread for local tab housekeeping only.
  useEffect(() => {
    if (thread.threadId && thread.threadId !== lastKnownThreadId) {
      if (lastKnownThreadId !== null) {
        try {
          localStorage.removeItem(LOCAL_STORAGE_TAB_KEY);
        } catch {
          // Ignore localStorage errors
        }
      }
      setLastKnownThreadId(thread.threadId);
    }
  }, [thread.threadId, lastKnownThreadId]);

  const handleCloseDetails = useCallback(
    async () => {
      await dispatch(
        executeSophiaAction({
          actionName: 'global-unset-open-details',
          refreshThread: true,
        }),
      );
    },
    [dispatch],
  );

  const handleRetryThread = useCallback(() => {
    dispatch(initializeThread());
  }, [dispatch]);

  const optimizationDraft = useMemo(
    () => (projectType === 'optimization' ? getOptimizationDraft(appState) : null),
    [appState, projectType],
  );
  const projectHasSucceededRun = useMemo(() => hasSucceededRun(appState), [appState]);

  /** Apre la MP da una run: nuova configurazione (fork) oppure, con `resume`, la bozza in corso. */
  const openMerchandisingPlanFromResult = useCallback((context: Partial<OptimizationResultRestoreContext> | undefined, resume: boolean) => {
    setOptimizationResultRestoreContext((prev) => ({
      runId: context?.runId ?? prev.runId,
      selectedShopId: context?.selectedShopId ?? prev.selectedShopId,
      selectedResultTab: context?.selectedResultTab ?? prev.selectedResultTab,
    }));
    setShouldRestoreOptimizationResultContext(true);
    setResumeOptimizationDraft(resume);
    setMerchandisingPlanEntryMode('from-result');
    setOptimizationView('merchandising-plan');
    setActiveTab('result');
  }, []);

  const handleOpenMerchandisingPlan = useCallback((context?: Partial<OptimizationResultRestoreContext>) => {
    if (optimizationDraft) {
      setPendingOptimizeContext(context ?? {});
      return;
    }
    openMerchandisingPlanFromResult(context, false);
  }, [openMerchandisingPlanFromResult, optimizationDraft]);

  const handleResumeOptimizationDraft = useCallback(() => {
    setPendingOptimizeContext(null);
    openMerchandisingPlanFromResult(undefined, true);
  }, [openMerchandisingPlanFromResult]);

  // SPJ-1366: aprendo (o ricaricando) un'ottimizzazione che ha solo una bozza, senza run da
  // mostrare, si va dritti alla bozza. Una volta per progetto: tornando al risultato si resta li'.
  const autoResumedThreadRef = useRef<string | null>(null);
  // Cambia a ogni caricamento esplicito dalla lista: fa ripartire l'effetto anche quando la vista
  // era gia' 'result' e onProjectLoaded la rimette uguale (stesso render, dipendenze invariate).
  const [projectLoadNonce, setProjectLoadNonce] = useState(0);
  useEffect(() => {
    const threadId = thread.threadId;
    if (!threadId || !thread.threadData || autoResumedThreadRef.current === threadId) return;
    if (projectType !== 'optimization' || optimizationView !== 'result' || isCreatingOptimizationProject) return;
    autoResumedThreadRef.current = threadId;
    if (optimizationDraft && !projectHasSucceededRun) {
      openMerchandisingPlanFromResult(undefined, true);
    }
  }, [isCreatingOptimizationProject, openMerchandisingPlanFromResult, optimizationDraft, optimizationView, projectHasSucceededRun, projectLoadNonce, projectType, thread.threadData, thread.threadId]);

  const handleBackToOptimization = useCallback(() => {
    setShouldRestoreOptimizationResultContext(true);
    setOptimizationView('result');
    setActiveTab('result');
  }, []);

  const handleStartNewOptimization = useCallback(() => {
    setPendingOptimizationCatalogMode('');
    setPendingOptimizationIncognito(false);
    setIsCreatingOptimizationProject(false);
    setSelectedProjectType('optimization');
    setNewProjectOpen(true);
    setProjectTypeSelectorOpen(false);
    setMerchandisingPlanEntryMode('new-optimization');
    setResumeOptimizationDraft(false);
    setShouldRestoreOptimizationResultContext(false);
    setOptimizationView('result');
    setActiveTab('result');
  }, []);

  const handleConfirmOptimizationCatalogMode = useCallback((catalogMode: CatalogMode, incognito: boolean) => {
    setPendingOptimizationCatalogMode(catalogMode);
    setPendingOptimizationIncognito(incognito);
    setIsCreatingOptimizationProject(true);
    setSelectedProjectType(null);
    setNewProjectOpen(false);
    setProjectTypeSelectorOpen(false);
    setMerchandisingPlanEntryMode('new-optimization');
    setResumeOptimizationDraft(false);
    setShouldRestoreOptimizationResultContext(false);
    setOptimizationView('merchandising-plan');
    setActiveTab('result');
  }, []);

  const handleOpenProjectTypeSelector = useCallback(() => {
    // SPJ-1372: "Nuovo progetto" lascia l'editor MP; se ci sono modifiche non salvate chiedi conferma.
    if (!confirmLeaveUnsavedMp()) return;
    setSelectedProjectType(null);
    setPendingOptimizationCatalogMode('');
    setPendingOptimizationIncognito(false);
    setIsCreatingOptimizationProject(false);
    setNewProjectOpen(false);
    setProjectTypeSelectorOpen(true);
  }, []);

  const handleOptimizationLaunched = useCallback(() => {
    setIsCreatingOptimizationProject(false);
    setSelectedProjectType(null);
    setPendingOptimizationCatalogMode('');
    setPendingOptimizationIncognito(false);
    setShouldRestoreOptimizationResultContext(false);
    setOptimizationView('result');
    setActiveTab('result');
  }, []);

  const handleEditTitle = useCallback(() => {
    setEditTitleValue(projectName || '');
    setIsEditingTitle(true);
  }, [projectName]);

  const handleSaveTitle = useCallback(async () => {
    if (editTitleValue.trim() && editTitleValue !== projectName) {
      await dispatch(
        executeSophiaAction({
          actionName: 'global-rename-project',
          params: { 'global-project-name': editTitleValue.trim() },
          refreshThread: true,
        }),
      );
    }
    setIsEditingTitle(false);
  }, [dispatch, editTitleValue, projectName]);

  const handleCancelEdit = useCallback(() => {
    setIsEditingTitle(false);
    setEditTitleValue('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  }, [handleSaveTitle, handleCancelEdit]);

  const goHome = useCallback(() => {
    setOptimizationView('result');
    setMerchandisingPlanEntryMode('new-optimization');
    setShouldRestoreOptimizationResultContext(false);
    setIsCreatingOptimizationProject(false);
    setSelectedProjectType(null);
    setIsAdminDashboardOpen(false);
    dispatch(resetThread());
  }, [dispatch]);

  const handleLogoClick = useCallback(() => {
    // SPJ-1372: tornare alla home abbandona l'editor MP; se ci sono modifiche non salvate chiedi conferma.
    if (!confirmLeaveUnsavedMp()) return;
    goHome();
  }, [goHome]);

  // SPJ-1372: finche' un'ottimizzazione e' aperta se ne tiene il lock, cosi' nessun altro la apre.
  // Le ottimizzazioni vecchie possono non avere project_type: si riconoscono dall'id ottimizzazione.
  const lockedProject = useAppSelector(selectLockedProject);
  useProjectLock(thread.threadId, projectType === 'optimization' || Boolean(appState?.['optim-optimization-id']));
  const currentThreadIdRef = useRef(thread.threadId);
  currentThreadIdRef.current = thread.threadId;
  useEffect(() => {
    if (!lockedProject) return;
    // Lock perso sul progetto aperto (scaduto e preso da un altro utente): si torna alla home.
    if (lockedProject.threadId === currentThreadIdRef.current) {
      goHome();
    }
    // La lista mostra subito chi sta usando il progetto.
    dispatch(loadAvailableProjects());
  }, [lockedProject, goHome, dispatch]);

  const openExcelExportModal = useCallback(async () => {
    const threadId = thread.threadId;
    const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string;

    setExcelExportModalOpen(true);

    if (!threadId || !messageId || !currentSelectedRunId) {
      return;
    }

    try {
      setExcelExportLoadingDefaults(true);
      const [schemaResult, optimizationResult] = await Promise.all([
        performAction({
          threadId,
          messageId,
          actionName: 'optim-get-weborder-export-modal',
          params: {
            'optim-run-id': currentSelectedRunId,
          },
        }),
        performAction({
          threadId,
          messageId,
          actionName: 'optim-get-optimization-result',
          params: { 'optim-run-id': currentSelectedRunId },
        }),
      ]);

      const schema = (schemaResult as { data?: Record<string, Record<string, Array<{ name: string; default?: string | number }>>> } | null)?.data;
      const optimizationData = optimizationResult as {
        metrics_per_consegna_per_shop?: Record<string, Array<{ name?: string }>>;
        shop_mapping?: Record<string, string>;
      } | null;

      const consegne = Array.from(
        new Set(
          Object.values(optimizationData?.metrics_per_consegna_per_shop || {})
            .flatMap((shopConsegnas) => (shopConsegnas || []).map((consegna) => String(consegna?.name || '').trim()))
            .filter((consegna) => consegna.length > 0 && consegna.toLowerCase() !== 'all'),
        ),
      );

      setExcelExportConsegne(consegne);

      // SPJ-891: raccogli i negozi aggregati (id con "|,|") e i loro componenti,
      // così la modale può offrire la scelta dei raggruppamenti.
      const shopMapping = optimizationData?.shop_mapping || {};
      const cleanShopId = (raw: string) => String(raw || '').replace(/\|/g, '').trim();
      const aggregatedShops = Object.keys(optimizationData?.metrics_per_consegna_per_shop || {})
        .filter((key) => key.includes('|,|') && !isAreaShop(key, shopMapping))
        .map((sourceId) => ({
          sourceId,
          members: sourceId
            .split('|,|')
            .map(cleanShopId)
            .filter((id) => id.length > 0 && !isAreaShop(id, shopMapping))
            .map((id) => ({ id, label: shopMapping[id] || id })),
        }))
        .filter((entry) => entry.members.length > 1);
      setExcelExportAggregatedShops(aggregatedShops);
      setExcelExportShopGroups({});

      setExcelExportForm((prev) => {
        const next: Record<string, ExcelExportConsegnaFormValue> = {};
        consegne.forEach((consegna) => {
          const current = prev[consegna];
          next[consegna] = {
            tipologiaOrdine:
              current?.tipologiaOrdine ||
              findFieldDefaultForConsegna(schema, consegna, ['tipologia_ordine']),
            codiceConsegna:
              current?.codiceConsegna ||
              findFieldDefaultForConsegna(schema, consegna, ['codice_consegna', 'consegna']),
          };
        });
        return next;
      });
    } catch (err) {
      console.error('Error loading Excel export defaults:', err);
    } finally {
      setExcelExportLoadingDefaults(false);
    }
  }, [thread.threadId, thread.threadData, currentSelectedRunId]);

  // Excel export handler
  const handleExportExcel = useCallback(async () => {
    try {
      const threadId = thread.threadId;
      const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string;

      if (!threadId || !messageId || !currentSelectedRunId) {
        alert("Impossibile esportare: dati mancanti");
        return;
      }

      if (
        excelExportConsegne.some((consegna) => {
          const values = excelExportForm[consegna];
          return !values?.tipologiaOrdine.trim() || !values?.codiceConsegna.trim();
        })
      ) {
        alert('Compila tipologia ordine e codice consegna per ogni consegna prima di esportare');
        return;
      }

      let result = await performAction({
        threadId,
        messageId,
        actionName: 'optim-get-optimization-result',
        params: { 'optim-run-id': currentSelectedRunId },
      }) as any;

      if (!result || !result.metrics_per_consegna_per_shop) {
        if (!result || !result.metrics_per_consegna) {
          alert("Nessun dato disponibile per l'esportazione");
          return;
        }
        result.metrics_per_consegna_per_shop = { Default: result.metrics_per_consegna };
      }

      if (excelExportSplitAggregatedShops) {
        try {
          const splitResult = await performAction({
            threadId,
            messageId,
            actionName: 'optim-split-aggregated-export-orders',
            params: {
              'optim-run-id': currentSelectedRunId,
              'metrics-per-consegna-per-shop': result.metrics_per_consegna_per_shop,
              'shop-mapping': result.shop_mapping,
              'shop-groups': excelExportShopGroups,
            },
          }) as any;
          if (splitResult?.metrics_per_consegna_per_shop && splitResult?.split_applied) {
            result = {
              ...result,
              metrics_per_consegna_per_shop: splitResult.metrics_per_consegna_per_shop,
              shop_mapping: {
                ...(result.shop_mapping || {}),
                ...(splitResult.shop_mapping || {}),
              },
              export_split_summary: splitResult.split_summary || [],
            };
          }
        } catch (splitErr) {
          console.warn('Aggregated-shop Excel export split not applied:', splitErr);
        }
      }

      const genderRaw = String(appState?.['optim-gender'] ?? 'Donna').toLowerCase();
      const isUomo = genderRaw.includes('uomo');
      const templateUrl = isUomo ? UOMO_TEMPLATE_URL : DONNA_TEMPLATE_URL;
      const templateBuffer = await fetch(templateUrl).then((response) => response.arrayBuffer());
      const templateWorkbook = XLSX.read(templateBuffer, { type: 'array', cellStyles: true });
      const templateSheetName = templateWorkbook.SheetNames[0];
      const templateSheet = templateWorkbook.Sheets[templateSheetName];

      if (!templateSheet) {
        throw new Error('Template Excel non trovato');
      }

      const shopIds = Object.keys(result.metrics_per_consegna_per_shop as Record<string, any[]>)
        .filter((shopId) => !isAreaShop(shopId, result.shop_mapping));
      if (!shopIds.length) {
        alert("Nessun negozio disponibile per l'esportazione");
        return;
      }

      const completeExportInfoRaw = await performAction({
        threadId,
        messageId,
        actionName: 'add-missing-information-for-export',
        params: {
          'sku-list': Array.from(
            new Set(
              Object.values(result.metrics_per_consegna_per_shop as Record<string, any[]>)
                .flatMap((shopConsegnas) => (shopConsegnas || []))
                .flatMap((consegna) => (consegna?.tf || []))
                .flatMap((tf: any) => (tf?.sku || []).map((sku: any) => String(sku?.name || '').trim()))
                .filter((sku) => sku.length > 0),
            ),
          ),
        },
      }) as Array<{ sku?: string; cod_modello?: string | null; cod_colore?: string | null; des_modello?: string | null; des_colore?: string | null }> | null;

      const completeExportInfo = new Map<string, { codModello: string; codColore: string; desModello: string; desColore: string }>();
      (completeExportInfoRaw || []).forEach((item) => {
        const sku = String(item?.sku || '').trim();
        if (!sku) return;
        completeExportInfo.set(sku, {
          codModello: String(item?.cod_modello ?? '').trim(),
          codColore: String(item?.cod_colore ?? '').trim(),
          desModello: String(item?.des_modello ?? '').trim(),
          desColore: String(item?.des_colore ?? '').trim(),
        });
      });
      const { totalSections, sectionsByConsegna, codTipoTagliaList } = buildAggregatedExportRows(result, completeExportInfo);
      const normalizedCodTipoTagliaList = Array.from(new Set(
        codTipoTagliaList
          .map((codTipoTaglia) => normalizeCodTipoTaglia(codTipoTaglia))
          .filter((codTipoTaglia) => codTipoTaglia.length > 0),
      )).sort((a, b) => a.localeCompare(b, 'it', { numeric: true }));
      const rawTaglieInfo = await performAction({
        threadId,
        messageId,
        actionName: 'get-taglie-info',
        params: {
          cod_tipo_taglia_list: normalizedCodTipoTagliaList,
        },
      }) as TaglieInfoEntry[] | null;

      const taglieInfoByCod = new Map<string, ResolvedTaglieInfoEntry>();
      const unresolvedCodici: string[] = [];
      (rawTaglieInfo || []).forEach((entry) => {
        const codTipoTaglia = normalizeCodTipoTaglia(entry?.cod_tipo_taglia);
        if (!codTipoTaglia) return;
        if (entry?.error) {
          unresolvedCodici.push(`${codTipoTaglia}: ${entry.error}`);
          return;
        }

        const iArr = I_ARR_OVERRIDES[codTipoTaglia] ?? Number(entry?.i_arr);
        const iArrTaglia = Number(entry?.i_arr_taglia);
        const grigliaTaglie = Array.isArray(entry?.griglia_taglie)
          ? entry.griglia_taglie.map((size) => normalizeSizeKey(size)).filter((size) => size.length > 0)
          : [];

        if (!Number.isFinite(iArr) || iArr <= 0) {
          unresolvedCodici.push(`${codTipoTaglia}: i_arr non valido`);
          return;
        }
        if (!Number.isFinite(iArrTaglia) || iArrTaglia <= 0) {
          unresolvedCodici.push(`${codTipoTaglia}: i_arr_taglia non valido`);
          return;
        }
        if (!grigliaTaglie.length) {
          unresolvedCodici.push(`${codTipoTaglia}: griglia_taglie vuota`);
          return;
        }
        if (grigliaTaglie.length > MAX_TEMPLATE_SIZE_COLS || iArrTaglia > MAX_TEMPLATE_SIZE_COLS) {
          unresolvedCodici.push(`${codTipoTaglia}: griglia superiore a ${MAX_TEMPLATE_SIZE_COLS} colonne`);
          return;
        }

        taglieInfoByCod.set(codTipoTaglia, {
          cod_tipo_taglia: codTipoTaglia,
          griglia_taglie: grigliaTaglie,
          i_arr: iArr,
          i_arr_taglia: iArrTaglia,
        });
      });

      normalizedCodTipoTagliaList.forEach((codTipoTaglia) => {
        if (!taglieInfoByCod.has(codTipoTaglia) && !unresolvedCodici.some((entry) => entry.startsWith(`${codTipoTaglia}:`))) {
          unresolvedCodici.push(`${codTipoTaglia}: nessuna risposta dal backend`);
        }
      });

      // Per-SKU-grid mode writes each row against its own cod_tipo_taglia grid,
      // so it skips the i_arr aggregation entirely (no merging, no shared 16-col
      // slot, hence no "colonne disponibili" overflow). `mergedSlots` stays empty
      // and createExportSheet reads each row's own griglia_taglie instead.
      //
      // Default (aggregated) mode: codes that share an i_arr slot get their grids
      // MERGED into one ordered, alias-collapsed size row (instead of the old
      // "one grid per slot, reject on conflict"). Sizes with no quantity anywhere
      // are dropped only if the merged row would exceed the template's column
      // count; if more than MAX_TEMPLATE_SIZE_COLS sizes actually carry quantity,
      // we fail honestly.
      const mergedSlots = new Map<number, MergedColumn[]>();
      if (!excelExportPerSkuGrid) {
        const codesBySlot = new Map<number, ResolvedTaglieInfoEntry[]>();
        taglieInfoByCod.forEach((entry) => {
          const slot = codesBySlot.get(entry.i_arr);
          if (slot) slot.push(entry);
          else codesBySlot.set(entry.i_arr, [entry]);
        });

        // Which sizes (by identity) carry quantity, per slot, across the whole export.
        const usedIdentitiesBySlot = new Map<number, Set<string>>();
        const collectUsage = (rows: AggregatedExportRow[]) => {
          rows.forEach((row) => {
            const info = taglieInfoByCod.get(row.codTipoTaglia);
            if (!info) return;
            let used = usedIdentitiesBySlot.get(info.i_arr);
            if (!used) { used = new Set<string>(); usedIdentitiesBySlot.set(info.i_arr, used); }
            Object.entries(row.taglieTotals).forEach(([size, qty]) => {
              if (Number(qty) > 0) used!.add(sizeIdentity(size));
            });
          });
        };
        collectUsage(totalSections.flatMap((section) => section.rows));
        sectionsByConsegna.forEach((sections) => collectUsage(sections.flatMap((section) => section.rows)));

        codesBySlot.forEach((codes, iArr) => {
          let columns = buildSlotColumns(codes);
          if (columns.length > MAX_TEMPLATE_SIZE_COLS) {
            const used = usedIdentitiesBySlot.get(iArr) || new Set<string>();
            columns = columns.filter((column) => used.has(column.identity));
            if (columns.length > MAX_TEMPLATE_SIZE_COLS) {
              unresolvedCodici.push(
                `i_arr ${iArr}: ${columns.length} taglie con quantità superano le ${MAX_TEMPLATE_SIZE_COLS} colonne disponibili (${columns.map((column) => column.label).join(', ')})`,
              );
            }
          }
          mergedSlots.set(iArr, columns);
        });
      }

      if (unresolvedCodici.length > 0) {
        throw new Error(`Impossibile esportare. Codici tipo taglia non risolti:\n${unresolvedCodici.join('\n')}`);
      }

      const outputWorkbook = XLSX.utils.book_new();
      const templateHeaderRow = findHeaderRow(templateSheet);
      const templateLayoutRows = getLayoutRowsByIndex(templateSheet, templateHeaderRow, BASE_BLOCK_START_COL);
      const firstSizeCol = BASE_BLOCK_START_COL + 1;
      const totalQtyCol = BASE_BLOCK_START_COL + 17;
      const totalValCol = BASE_BLOCK_START_COL + 18;

      // Prefer the size label already pre-printed in the template's legend row
      // for a slot (e.g. "XXX" over "3XL"); fall back to the first-seen form.
      mergedSlots.forEach((columns, iArr) => {
        const legendRow = templateLayoutRows[iArr];
        if (!legendRow) return;
        const printedByIdentity = new Map<string, string>();
        for (let offset = 0; offset < MAX_TEMPLATE_SIZE_COLS; offset += 1) {
          const raw = templateSheet[XLSX.utils.encode_cell({ r: legendRow - 1, c: firstSizeCol - 1 + offset })]?.v;
          if (raw === undefined || raw === null || raw === '') continue;
          if (sizeSortKey(raw)[0] === SF.Drop) continue;
          printedByIdentity.set(sizeIdentity(raw), String(raw));
        }
        columns.forEach((column) => {
          const printed = printedByIdentity.get(column.identity);
          if (printed) column.label = printed;
        });
      });

      const sheetNameCounts = new Map<string, number>();

      const appendUniqueSheet = (baseName: string, ws: XLSX.WorkSheet) => {
        const sanitizedBase = sanitizeSheetName(baseName);
        const count = (sheetNameCounts.get(sanitizedBase) || 0) + 1;
        sheetNameCounts.set(sanitizedBase, count);
        const sheetName = count === 1 ? sanitizedBase : sanitizeSheetName(`${sanitizedBase}_${count}`);
        XLSX.utils.book_append_sheet(outputWorkbook, ws, sheetName);
      };

      // Uno sheet e' fatto di uno o piu' GRUPPI. Un gruppo e' una consegna con le sue
      // sezioni (una per negozio). Gli sheet per consegna e il "Totale" hanno un gruppo
      // solo; "Totale consegne" li ha tutti, impilati: le righe diventano una per
      // (consegna, SKU) invece che una per SKU, cosi' lo stesso capo compare una volta
      // per consegna e il TOTALISSIMO in fondo somma tutto.
      const createExportSheet = (
        sheetLabel: string,
        groups: ExportGroup[],
        includeConsegnaFields: boolean,
        // Salta le righe senza capi. Il totale si conta su TUTTI i negozi: una riga sparisce
        // solo se e' a zero ovunque, non se lo e' in un negozio soltanto.
        dropEmptyRows = false,
      ) => {
        const ws = JSON.parse(JSON.stringify(templateSheet)) as XLSX.WorkSheet;
        const headerRow = findHeaderRow(ws);
        const dataStartRow = headerRow + 1;

        // I blocchi colonna restano uno per negozio, in unione su tutti i gruppi: una
        // riga ha quantita' solo nel proprio gruppo, nelle altre resta vuota.
        const sectionByShop = new Map<string, { key: string; label: string }>();
        groups.forEach((group) => {
          group.sections.forEach((section) => {
            if (!sectionByShop.has(section.key)) {
              sectionByShop.set(section.key, { key: section.key, label: section.label });
            }
          });
        });
        const sections = Array.from(sectionByShop.values())
          .sort((a, b) => a.label.localeCompare(b.label, 'it', { numeric: true }));

        // Chiave di riga: indice del gruppo + SKU, cosi' consegne diverse non si fondono.
        const rowKey = (groupIndex: number, skuName: string) => `${groupIndex} ${skuName}`;
        let rowOrder: string[] = [];
        const rowMeta = new Map<string, AggregatedExportRow>();
        const rowGroupIndex = new Map<string, number>();
        // Capi totali della riga sommati su tutti i negozi del suo gruppo.
        const rowQuantity = new Map<string, number>();
        groups.forEach((group, groupIndex) => {
          group.sections.forEach((section) => {
            section.rows.forEach((row) => {
              const key = rowKey(groupIndex, row.skuName);
              if (!rowMeta.has(key)) {
                rowMeta.set(key, row);
                rowGroupIndex.set(key, groupIndex);
                rowOrder.push(key);
              }
              const quantita = Object.values(row.taglieTotals)
                .reduce((somma, valore) => somma + (Number(valore) || 0), 0);
              rowQuantity.set(key, (rowQuantity.get(key) || 0) + quantita);
            });
          });
        });
        if (dropEmptyRows) {
          rowOrder = rowOrder.filter((key) => (rowQuantity.get(key) || 0) > 0);
        }
        const totalRow = dataStartRow + rowOrder.length;
        const templateRange = XLSX.utils.decode_range(ws['!ref'] || 'A1:AD1');
        const blockStarts = sections.map((_, index) => BASE_BLOCK_START_COL + index * BLOCK_WIDTH_COLS);
        const maxBlockEndCol = (blockStarts[blockStarts.length - 1] || BASE_BLOCK_START_COL) + BLOCK_WIDTH_COLS - 1;
        const clearUntilRow = Math.max(templateRange.e.r + 1, totalRow + 1);
        const templateDataRow = dataStartRow;

        Object.keys(ws).forEach((key) => {
          if (key.startsWith('!')) return;
          const addr = XLSX.utils.decode_cell(key);
          if (addr.c > maxBlockEndCol - 1) {
            delete ws[key];
          }
        });

        ws['!merges'] = (ws['!merges'] || []).filter((merge) => merge.e.c <= maxBlockEndCol - 1);

        const firstBlockStart = BASE_BLOCK_START_COL;
        blockStarts.forEach((blockStart, index) => {
          if (index === 0) return;
          for (let row = 1; row <= headerRow; row += 1) {
            for (let offset = 0; offset < BLOCK_WIDTH_COLS; offset += 1) {
              const srcRef = XLSX.utils.encode_cell({ r: row - 1, c: firstBlockStart - 1 + offset });
              const dstRef = XLSX.utils.encode_cell({ r: row - 1, c: blockStart - 1 + offset });
              const srcCell = ws[srcRef];
              if (srcCell) {
                ws[dstRef] = JSON.parse(JSON.stringify(srcCell));
              } else {
                delete ws[dstRef];
              }
            }
          }
        });

        clearSheetValues(ws, dataStartRow, clearUntilRow, maxBlockEndCol);
        for (let row = dataStartRow; row <= totalRow; row += 1) {
          copyRowTemplate(ws, templateDataRow, row, maxBlockEndCol);
        }
        clearSheetValues(ws, dataStartRow, clearUntilRow, maxBlockEndCol);
        setCellValue(ws, 1, BASE_BLOCK_START_COL, sheetLabel);

        const presentSlots = [...mergedSlots.keys()].sort((a, b) => a - b);

        blockStarts.forEach((blockStart, sectionIndex) => {
          const section = sections[sectionIndex];
          const blockFirstSizeCol = blockStart + 1;
          const blockTotalQtyCol = blockStart + 17;
          const blockTotalValCol = blockStart + 18;
          setCellValue(ws, 1, blockStart, section.label || sheetLabel);

          presentSlots.forEach((iArr) => {
            const targetRow = templateLayoutRows[iArr];
            if (!targetRow) {
              throw new Error(`Il template Excel non supporta i_arr=${iArr}`);
            }
            const columns = mergedSlots.get(iArr) || [];
            for (let offset = 0; offset < MAX_TEMPLATE_SIZE_COLS; offset += 1) {
              setCellValue(ws, targetRow, blockFirstSizeCol + offset, '');
            }
            setCellValue(ws, targetRow, blockStart, iArr);
            columns.forEach((column, offset) => {
              setCellValue(ws, targetRow, blockFirstSizeCol + offset, column.label);
            });
          });

          // Le righe di questo blocco arrivano da tutti i gruppi, ciascuna dal proprio.
          const rowsByKey = new Map<string, AggregatedExportRow>();
          groups.forEach((group, groupIndex) => {
            const groupSection = group.sections.find((candidate) => candidate.key === section.key);
            groupSection?.rows.forEach((row) => {
              rowsByKey.set(rowKey(groupIndex, row.skuName), row);
            });
          });
          const totalsByColumn = new Array<number>(MAX_TEMPLATE_SIZE_COLS).fill(0);
          let grandTotalQty = 0;
          let grandTotalValue = 0;

          rowOrder.forEach((key, rowIndex) => {
            const excelRow = dataStartRow + rowIndex;
            const row = rowsByKey.get(key);
            if (!row) return;

            const taglieInfo = taglieInfoByCod.get(row.codTipoTaglia);
            if (!taglieInfo) {
              throw new Error(`Configurazione taglie mancante per cod_tipo_taglia ${row.codTipoTaglia}`);
            }

            let rowTotalQty = 0;
            if (excelExportPerSkuGrid) {
              // Per-SKU mode: place quantities at this row's OWN grid positions
              // (grl_taglia01 -> 1st size col, ...), no cross-SKU merging and no
              // dropping of empty slots. Match the row's sizes to its grid by
              // size identity so equivalent spellings (3XL/XXX, 40/40.0) still align.
              const qtyByIdentity = new Map<string, number>();
              Object.entries(row.taglieTotals).forEach(([size, qty]) => {
                const n = Number(qty || 0);
                if (!n) return;
                const id = sizeIdentity(size);
                qtyByIdentity.set(id, (qtyByIdentity.get(id) || 0) + n);
              });
              taglieInfo.griglia_taglie.forEach((size, index) => {
                if (index >= MAX_TEMPLATE_SIZE_COLS) return;
                const qty = qtyByIdentity.get(sizeIdentity(size)) || 0;
                if (!qty) return;
                setCellValue(ws, excelRow, blockFirstSizeCol + index, qty);
                rowTotalQty += qty;
                totalsByColumn[index] += qty;
              });
            } else {
              const columns = mergedSlots.get(taglieInfo.i_arr) || [];
              columns.forEach((column, index) => {
                let qty = 0;
                column.aliases.forEach((alias) => { qty += Number(row.taglieTotals[alias] || 0); });
                if (!qty) return;
                setCellValue(ws, excelRow, blockFirstSizeCol + index, qty);
                rowTotalQty += qty;
                totalsByColumn[index] += qty;
              });
            }

            const rowTotalValue = typeof row.whsPrice === 'number' ? rowTotalQty * row.whsPrice : 0;
            setCellValue(ws, excelRow, blockTotalQtyCol, rowTotalQty);
            setCellValue(ws, excelRow, blockTotalValCol, rowTotalValue || '');
            grandTotalQty += rowTotalQty;
            grandTotalValue += rowTotalValue;
          });

          // Per-column totals are meaningful only when every row shares one
          // column->size mapping. In per-SKU mode a column holds different sizes
          // per row, so the positional sum would be nonsense: leave those blank.
          if (!excelExportPerSkuGrid) {
            for (let offset = 0; offset < MAX_TEMPLATE_SIZE_COLS; offset += 1) {
              setCellValue(ws, totalRow, blockFirstSizeCol + offset, totalsByColumn[offset] || '');
            }
          }
          setCellValue(ws, totalRow, blockTotalQtyCol, grandTotalQty || '');
          setCellValue(ws, totalRow, blockTotalValCol, grandTotalValue || '');
        });

        rowOrder.forEach((key, rowIndex) => {
          const excelRow = dataStartRow + rowIndex;
          const row = rowMeta.get(key);
          if (!row) return;
          // I campi consegna vengono dal gruppo della riga: nello sheet concatenato ogni
          // riga porta la propria consegna, non una sola valida per tutto lo sheet.
          const consegnaName = groups[rowGroupIndex.get(key) ?? 0]?.consegna;
          const consegnaForm = consegnaName ? excelExportForm[consegnaName] : undefined;
          setCellValue(ws, excelRow, 1, includeConsegnaFields ? consegnaForm?.tipologiaOrdine || '' : '');
          setCellValue(ws, excelRow, 2, includeConsegnaFields ? consegnaForm?.codiceConsegna || '' : '');
          setCellValue(ws, excelRow, 3, row.tf);
          setCellValue(ws, excelRow, 4, row.resortSpring);
          setCellValue(ws, excelRow, 5, row.model);
          setCellValue(ws, excelRow, 6, row.color);
          setCellValue(ws, excelRow, 7, row.desColore);
          setCellValue(ws, excelRow, 8, row.desModello);
          setCellValue(ws, excelRow, 10, row.whsPrice);
          setCellValue(ws, excelRow, 11, row.retailPrice);
        });

        setCellValue(ws, totalRow, 3, 'TOTALISSIMO');

        const currentRange = XLSX.utils.decode_range(ws['!ref'] || 'A1:AD1');
        currentRange.e.r = Math.max(currentRange.e.r, totalRow - 1);
        currentRange.e.c = maxBlockEndCol - 1;
        ws['!ref'] = XLSX.utils.encode_range(currentRange);

        appendUniqueSheet(sheetLabel, ws);
      };

      createExportSheet('Totale', [{ sections: totalSections }], false);
      excelExportConsegne.forEach((consegna) => {
        createExportSheet(consegna, [{ consegna, sections: sectionsByConsegna.get(consegna) || [] }], true);
      });
      // Gli sheet delle consegne uno dietro l'altro, nello stesso ordine: stesse righe,
      // nessuna fusione fra consegne, e un TOTALISSIMO che somma tutto. Qui le righe senza
      // capi vengono saltate (a zero su tutti i negozi, non su uno solo).
      createExportSheet(
        'Totale consegne',
        excelExportConsegne.map((consegna) => ({
          consegna,
          sections: sectionsByConsegna.get(consegna) || [],
        })),
        true,
        true,
      );

      const timestamp = new Date().toISOString().slice(0, 10);
      const genderToken = isUomo ? 'uomo' : 'donna';
      const filename = `ottimizzazione_${genderToken}_run_${currentSelectedRunId}_${timestamp}.xlsx`;
      XLSX.writeFile(outputWorkbook, filename);
      setExcelExportModalOpen(false);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert(`Errore durante l'esportazione: ${(err as Error).message}`);
    }
  }, [thread.threadId, thread.threadData, currentSelectedRunId, appState, excelExportForm, excelExportConsegne, excelExportSplitAggregatedShops, excelExportPerSkuGrid, excelExportShopGroups]);

  const formatLastEditTime = useCallback((timestamp: number | null): string => {
    if (!timestamp) {
      return '01/10/2025'; // Fallback to original date
    }
    
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  const compareDisabled = selectedCount < 2;
  const forecastDisabled = selectedCount < 1;

  const tabs = useMemo(
    () => {
      if (projectType === 'optimization') {
        // No tabs for optimization projects - store/area selection is handled within OptimizationResultView
        return [];
      } else {
        return [
          { 
            id: 'list' as ConsultationTabKey, 
            label: 'Lista Prodotti', 
            disabled: false,
            tooltip: null
          },
          {
            id: 'compare' as ConsultationTabKey,
            label: 'Confronto Prodotti',
            disabled: compareDisabled,
            tooltip: compareDisabled ? 'Seleziona almeno 2 prodotti per confrontarli' : null
          },
          {
            id: 'forecast' as ConsultationTabKey,
            label: 'Forecast',
            disabled: forecastDisabled,
            tooltip: forecastDisabled ? 'Seleziona almeno 1 prodotto per la previsione' : null
          },
        ];
      }
    },
    [projectType, compareDisabled, forecastDisabled],
  );

  // Show thread error state
  if (threadStatus === 'failed' && threadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-center">
        <div className="max-w-md p-6">
          <div className="mb-4">
            <i className="ti-alert-triangle text-4xl text-red-500" aria-hidden="true" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Connection Error</h2>
          <p className="text-sm text-red-600 mb-4">{threadError}</p>
          <button
            type="button"
            onClick={handleRetryThread}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const showMerchandisingPlanPage = isCreatingOptimizationProject || (optimizationView === 'merchandising-plan' && projectType === 'optimization');

  // Show loading state unless we're intentionally entering the new optimization setup flow.
  if (threadStatus === 'loading' && !showMerchandisingPlanPage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-sm text-gray-600">
        <div className="text-center">
          <div className="mb-4">
            <i className="ti-loader animate-spin text-2xl text-gray-400" aria-hidden="true" />
          </div>
          <span>Initializing application…</span>
        </div>
      </div>
    );
  }


  // Show welcome page when no active project
  if (!hasActiveProject) {
    return (
      <>
        {showMerchandisingPlanPage ? (
          <MerchandisingPlanPage
            skipInitialLoad={isCreatingOptimizationProject}
            entryMode={merchandisingPlanEntryMode}
            resultContext={optimizationResultRestoreContext}
            initialCatalogMode={pendingOptimizationCatalogMode || undefined}
            initialIncognito={pendingOptimizationIncognito}
            onBackToOptimization={handleBackToOptimization}
            resumeDraft={resumeOptimizationDraft}
          />
        ) : (
          <WelcomePage onCreateProject={handleOpenProjectTypeSelector} />
        )}
        <ProjectTypeSelectorModal 
          isOpen={isProjectTypeSelectorOpen} 
          onClose={() => setProjectTypeSelectorOpen(false)}
          onSelectOptimization={handleStartNewOptimization}
          onSelectType={(type) => {
            setSelectedProjectType(type);
            setProjectTypeSelectorOpen(false);
            if (type === 'optimization') {
              handleStartNewOptimization();
              return;
            }
            setNewProjectOpen(true);
          }}
        />
        {selectedProjectType && (
          <NewProjectModal 
            isOpen={isNewProjectOpen} 
            projectType={selectedProjectType} 
            catalogModeOnly={selectedProjectType === 'optimization'}
            onCatalogModeConfirmed={handleConfirmOptimizationCatalogMode}
            isAdmin={isAdmin}
            onClose={() => {
              setNewProjectOpen(false);
              setSelectedProjectType(null);
              setPendingOptimizationCatalogMode('');
              setPendingOptimizationIncognito(false);
            }}
            onProjectCreated={() => {
              setNewProjectOpen(false);
              setSelectedProjectType(null);
              setPendingOptimizationCatalogMode('');
              setPendingOptimizationIncognito(false);
              setIsCreatingOptimizationProject(false);
              setMerchandisingPlanEntryMode('new-optimization');
              setShouldRestoreOptimizationResultContext(false);
              setOptimizationView('result');
              setActiveTab('list');
              dispatch(initializeThread());
            }}
            onOpenMerchandisingPlan={handleOpenMerchandisingPlan}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex h-screen bg-white text-black overflow-hidden">
      <div className={`flex w-full flex-col ${SIDEBAR_WIDTH_CLASS} relative`}>
        <div id="shrink-tabs" className="border-b border-gray-200 bg-white sticky top-0 z-30">
          <div className="px-4 py-3 flex items-center gap-2 align-middle group">
            <button 
              type="button"
              onClick={handleLogoClick}
              className='text-lg bg-[#9F6460] text-white px-2 mr-2 py-1 font-semibold flex-row flex items-center gap-x-2 hover:bg-[#8a5854] transition-colors cursor-pointer'
              title="Torna alla home"
            >
              <img className='h-5' src={Logo} alt="Logo" />
              <div>PREDICTIVE MERCHANDISING</div>
            </button>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {isEditingTitle ? (
                <input
                  type="text"
                  value={editTitleValue}
                  onChange={(e) => setEditTitleValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSaveTitle}
                  autoFocus
                  className="border-b border-gray-300 bg-transparent px-1 py-0.5 text-lg font-semibold focus:border-blue-500 focus:outline-none"
                />
              ) : (
                <>
                  {projectName ? (
                    <div className="truncate text-lg font-semibold">{projectName}</div>
                  ) : (
                    <div className="truncate text-lg italic">Workspace Senza Titolo</div>
                  )}
                  <button
                    type="button"
                    onClick={handleEditTitle}
                    className="flex h-6 w-0 items-center justify-center overflow-hidden p-0 opacity-0 transition-all duration-200 group-hover:w-8 group-hover:p-1 group-hover:opacity-100 hover:bg-gray-100"
                    title="Edit project name"
                  >
                    <i className="ti-pencil text-sm text-gray-500 hover:text-gray-700" aria-hidden="true" />
                  </button>
                </>
              )}
              <div className="text-sm transition-transform duration-200 group-hover:translate-x-2">{formatLastEditTime(lastEditTime)}</div>
            </div>
            {currentCatalogMode && (
              <div className="ml-auto shrink-0 border border-gray-300 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                {currentCatalogMode} · {getCatalogModeLabel(currentCatalogMode)}
              </div>
            )}
          </div>
          <div className="flex w-full">
            {tabs.map(tab => (
              <div key={tab.id} className="relative flex items-center">
                <button
                  type="button"
                  onClick={() => setActiveTabWithStorage(tab.id)}
                  disabled={tab.disabled}
                  className={`px-4 py-3 text-sm font-semibold flex flex-row items-center gap-x-1 transition-colors ${
                    activeTab === tab.id
                      ? 'border-b-2 border-black text-black'
                      : 'border-b-2 border-transparent text-gray-500 hover:text-black'
                  } ${tab.disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  {tab.label}
                    {tab.disabled && tab.tooltip && (
                    <div className="relative ml-0.5 group">
                        <div className="flex items-center justify-center w-4 h-4 text-xs text-gray-400 border border-gray-300 rounded-full cursor-help">
                        ?
                        </div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        {tab.tooltip}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                        </div>
                    </div>
                    )}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 flex flex-col w-full overflow-y-auto">
          {isAdminDashboardOpen && isAdmin ? (
            activeAdminTab === 'mappings' ? (
              <AdminMappingsDashboard
                threadId={thread.threadId || ''}
                messageId={(thread.threadData?.local_memory?.thread_state?.['current-message'] as string) || ''}
                onAdminAlertChange={setHasAdminNotification}
                onAdminAlertReasonsChange={setAdminNotificationItems}
              />
            ) : activeAdminTab === 'forecast' ? (
              <AdminForecastDashboard
                threadId={thread.threadId || ''}
                messageId={(thread.threadData?.local_memory?.thread_state?.['current-message'] as string) || ''}
              />
            ) : null
          ) : showMerchandisingPlanPage ? (
            <MerchandisingPlanPage
              onOptimizationLaunched={handleOptimizationLaunched}
              onBackToOptimization={handleBackToOptimization}
              skipInitialLoad={isCreatingOptimizationProject}
              entryMode={merchandisingPlanEntryMode}
              resultContext={optimizationResultRestoreContext}
              initialCatalogMode={pendingOptimizationCatalogMode || undefined}
              initialIncognito={pendingOptimizationIncognito}
              resumeDraft={resumeOptimizationDraft}
            />
          ) : projectType === 'optimization' ? (
            <>
              {optimizationView === 'result' && activeTab === 'result' && optimizationDraft && projectHasSucceededRun && (
                <div className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-3 border border-[#9F6460]/40 bg-[#9F6460]/5 px-4 py-3 text-sm text-gray-800">
                  <span>Hai una configurazione in corso non ancora ottimizzata.</span>
                  <button
                    type="button"
                    onClick={handleResumeOptimizationDraft}
                    className="bg-black px-3 py-1.5 text-xs font-semibold text-white hover:opacity-80"
                  >
                    RIPRENDI
                  </button>
                </div>
              )}
              <Modal isOpen={pendingOptimizeContext !== null} onClose={() => setPendingOptimizeContext(null)}>
                <div className="w-[min(440px,90vw)] p-6">
                  <h2 className="text-base font-semibold text-gray-900">Hai una configurazione in corso</h2>
                  <p className="mt-2 text-sm text-gray-600">
                    Puoi riprendere la configurazione che non hai ancora ottimizzato, oppure crearne una nuova da questa run.
                    La nuova sostituisce quella in corso.
                  </p>
                  <div className="mt-5 flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={handleResumeOptimizationDraft}
                      className="bg-black px-4 py-2 text-sm font-semibold text-white hover:opacity-80"
                    >
                      Riprendi la configurazione in corso
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const context = pendingOptimizeContext ?? undefined;
                        setPendingOptimizeContext(null);
                        openMerchandisingPlanFromResult(context, false);
                      }}
                      className="border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:border-gray-900"
                    >
                      Nuova configurazione da questa run
                    </button>
                  </div>
                </div>
              </Modal>
              {optimizationView === 'result' && activeTab === 'result' && (
                <OptimizationResultView
                  onOpenMerchandisingPlan={handleOpenMerchandisingPlan}
                  initialRunId={shouldRestoreOptimizationResultContext ? optimizationResultRestoreContext.runId : null}
                  initialSelectedShopId={shouldRestoreOptimizationResultContext ? optimizationResultRestoreContext.selectedShopId : null}
                  initialActiveTab={shouldRestoreOptimizationResultContext ? optimizationResultRestoreContext.selectedResultTab : undefined}
                />
              )}
            </>
          ) : (
            <>
              {activeTab === 'list' && <ListProducts/>}
              {activeTab === 'compare' && <SimilarityMatrix />}
              {activeTab === 'forecast' && <ForecastV2 />}
            </>
          )}
        </div>
      </div>
      <Sidebar
        selectedCount={selectedCount}
        onOpenSelection={() => setStateOpen(true)}
        onOpenStorage={() => setStorageOpen(true)}
        onOpenNewProject={handleOpenProjectTypeSelector}
        onOpenProfile={() => setProfileOpen(true)}
        onOpenFeedback={() => setFeedbackOpen(true)}
        onOpenAdmin={() => {
          if (!isAdmin) return;
          setActiveAdminTab('mappings');
          setIsAdminDashboardOpen(true);
        }}
        isAdmin={isAdmin}
        hasAdminNotification={hasAdminNotification}
        adminNotificationItems={adminNotificationItems}
        isAdminMode={isAdmin && isAdminDashboardOpen}
        activeAdminPage={activeAdminTab}
        onOpenAdminMappings={() => {
          if (!isAdmin) return;
          setActiveAdminTab('mappings');
          setIsAdminDashboardOpen(true);
        }}
        onOpenAdminForecast={() => {
          if (!isAdmin) return;
          setActiveAdminTab('forecast');
          setIsAdminDashboardOpen(true);
        }}
        onExitAdmin={() => setIsAdminDashboardOpen(false)}
        projectType={projectType}
        onExportExcel={openExcelExportModal}
        onExportWeborder={() => setWeborderExportModalOpen(true)}
        canExport={canExport}
      />
      <DetailsModal
        sku={detailsSku}
        isOpen={!!detailsSku}
        onClose={() => handleCloseDetails()}
        projectType={projectType}
      />
      <StateModal isOpen={isStateOpen} onClose={() => setStateOpen(false)} />
      <StorageModal
        isOpen={isStorageOpen}
        onClose={() => setStorageOpen(false)}
        onProjectLoaded={(loadedProjectType) => {
          // SPJ-1366: caricamento esplicito, si rivaluta la bozza. L'effetto di ripresa potrebbe
          // essere gia' scattato (loadProject arriva prima di questo callback, che rimette la vista
          // sul risultato): senza reset il progetto resterebbe "gia' gestito".
          autoResumedThreadRef.current = null;
          setProjectLoadNonce((n) => n + 1);
          setResumeOptimizationDraft(false);
          setOptimizationView('result');
          setMerchandisingPlanEntryMode('new-optimization');
          setShouldRestoreOptimizationResultContext(false);
          setIsCreatingOptimizationProject(false);
          setSelectedProjectType(null);
          setProjectTypeSelectorOpen(false);
          setNewProjectOpen(false);
          setActiveTab(loadedProjectType === 'optimization' ? 'result' : 'list');
        }}
      />
      <ProjectTypeSelectorModal 
        isOpen={isProjectTypeSelectorOpen} 
        onClose={() => setProjectTypeSelectorOpen(false)}
        onSelectOptimization={handleStartNewOptimization}
        onSelectType={(type) => {
          setSelectedProjectType(type);
          setProjectTypeSelectorOpen(false);
          if (type === 'optimization') {
            handleStartNewOptimization();
            return;
          }
          setNewProjectOpen(true);
        }}
      />
      {selectedProjectType && (
        <NewProjectModal 
          isOpen={isNewProjectOpen} 
          projectType={selectedProjectType} 
          catalogModeOnly={selectedProjectType === 'optimization'}
          onCatalogModeConfirmed={handleConfirmOptimizationCatalogMode}
          isAdmin={isAdmin}
          onClose={() => {
            setNewProjectOpen(false);
            setSelectedProjectType(null);
            setPendingOptimizationCatalogMode('');
            setPendingOptimizationIncognito(false);
          }}
          onProjectCreated={() => {
            setNewProjectOpen(false);
            setSelectedProjectType(null);
            setPendingOptimizationCatalogMode('');
            setPendingOptimizationIncognito(false);
            setIsCreatingOptimizationProject(false);
            setMerchandisingPlanEntryMode('new-optimization');
            setShouldRestoreOptimizationResultContext(false);
            setOptimizationView('result');
            setActiveTab('list');
            dispatch(initializeThread());
          }}
          onOpenMerchandisingPlan={handleOpenMerchandisingPlan}
        />
      )}
      <ProfileModal isOpen={isProfileOpen} onClose={() => setProfileOpen(false)} />
      <AlertModal
        isOpen={lockedProject !== null}
        onClose={() => dispatch(clearLockedProject())}
        type="warning"
        title="Progetto in uso"
        message={`Il progetto è aperto da ${lockedProject?.lockedByName || lockedProject?.lockedByEmail || 'un altro utente'}. Potrai aprirlo quando lo avrà chiuso.`}
      />
      <FeedbackModal 
        isOpen={isFeedbackOpen} 
        onClose={() => setFeedbackOpen(false)} 
        projectType={projectType}
      />
      <Modal
        isOpen={isExcelExportModalOpen}
        onClose={() => {
          if (isExcelExportLoadingDefaults) return;
          setExcelExportModalOpen(false);
        }}
      >
        <div className="min-w-[360px] max-w-[520px] p-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Esporta Excel</h2>
              <p className="mt-1 text-sm text-gray-600">
                Compila i campi che verranno scritti nel template Excel.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setExcelExportModalOpen(false)}
              className="text-2xl leading-none text-gray-400 hover:text-gray-700"
              aria-label="Chiudi modale export excel"
            >
              ×
            </button>
          </div>
          <div className="space-y-4">
            {!isExcelExportLoadingDefaults && excelExportConsegne.length === 0 && (
              <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                Nessuna consegna disponibile per l'export.
              </div>
            )}
            {excelExportConsegne.map((consegna) => (
              <div key={consegna} className="rounded border border-gray-200 p-3">
                <div className="mb-3 text-sm font-semibold text-gray-900">
                  {consegna.replace(/_/g, ' ').toUpperCase()}
                </div>
                <div className="space-y-3">
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-gray-700">#PO / Tipologia ordine</span>
                    <input
                      type="text"
                      value={excelExportForm[consegna]?.tipologiaOrdine || ''}
                      onChange={(e) =>
                        setExcelExportForm((prev) => ({
                          ...prev,
                          [consegna]: {
                            ...(prev[consegna] || { tipologiaOrdine: '', codiceConsegna: '' }),
                            tipologiaOrdine: e.target.value,
                          },
                        }))
                      }
                      className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Inserisci tipologia ordine"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-gray-700">CLASS / Codice consegna</span>
                    <input
                      type="text"
                      value={excelExportForm[consegna]?.codiceConsegna || ''}
                      onChange={(e) =>
                        setExcelExportForm((prev) => ({
                          ...prev,
                          [consegna]: {
                            ...(prev[consegna] || { tipologiaOrdine: '', codiceConsegna: '' }),
                            codiceConsegna: e.target.value,
                          },
                        }))
                      }
                      className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Inserisci codice consegna"
                    />
                  </label>
                </div>
              </div>
            ))}
            <label className="flex items-start gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={excelExportSplitAggregatedShops}
                onChange={(event) => setExcelExportSplitAggregatedShops(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
              />
              <span>
                <span className="block font-medium text-gray-900">Dividi negozi aggregati</span>
                <span className="block text-gray-600">
                  Se attivo, un negozio composto da più codici viene esportato come negozi separati usando i budget per consegna.
                  Se non attivo, il negozio resta aggregato in un unico blocco Excel.
                </span>
              </span>
            </label>
            {excelExportSplitAggregatedShops && excelExportAggregatedShops.length > 0 && (
              <div className="space-y-3 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
                <div>
                  <span className="block font-medium text-gray-900">Raggruppamenti negozi (opzionale)</span>
                  <span className="block text-gray-600">
                    Assegna la stessa etichetta a più negozi per esportarli come un unico blocco: il budget del gruppo
                    è la somma dei budget dei negozi. Lascia vuoto per tenere il negozio separato.
                  </span>
                </div>
                {excelExportAggregatedShops.map((agg) => (
                  <div key={agg.sourceId} className="space-y-1.5">
                    {excelExportAggregatedShops.length > 1 && (
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                        {agg.members.map((m) => m.label).join(' + ')}
                      </div>
                    )}
                    {agg.members.map((m) => (
                      <div key={m.id} className="flex items-center justify-between gap-3">
                        <span className="min-w-0 flex-1 truncate text-gray-800" title={m.label}>{m.label}</span>
                        <input
                          type="text"
                          value={excelExportShopGroups[m.id] || ''}
                          onChange={(event) =>
                            setExcelExportShopGroups((prev) => ({ ...prev, [m.id]: event.target.value }))
                          }
                          placeholder="gruppo"
                          className="w-24 flex-shrink-0 rounded border border-gray-300 px-2 py-1 text-right placeholder:text-gray-400 focus:border-gray-900 focus:outline-none"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <label className="flex items-start gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={excelExportPerSkuGrid}
                onChange={(event) => setExcelExportPerSkuGrid(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
              />
              <span>
                <span className="block font-medium text-gray-900">Modalità per griglia SKU (no aggregazione i_arr)</span>
                <span className="block text-gray-600">
                  Se attivo, ogni SKU scrive le quantità nelle posizioni della propria griglia taglie (cod_tipo_taglia),
                  senza aggregare/unire griglie diverse sullo stesso slot i_arr. L'intestazione del template resta invariata.
                  Se non attivo, si usa la logica attuale con aggregazione per slot i_arr.
                </span>
              </span>
            </label>
            <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              <span className="font-medium text-gray-800">Resort/Spring</span>
              {' '}
              viene valorizzato automaticamente per riga in base ai tag weborder del prodotto:
              {' '}
              <code>spring -&gt; S</code>, <code>resort -&gt; R</code>.
            </div>
            {isExcelExportLoadingDefaults && (
              <p className="text-sm text-gray-500">Caricamento valori di default dalla configurazione Weborder...</p>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setExcelExportModalOpen(false)}
              className="border border-black bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-gray-100"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={excelExportConsegne.length === 0 || excelExportConsegne.some((consegna) => !excelExportForm[consegna]?.tipologiaOrdine.trim() || !excelExportForm[consegna]?.codiceConsegna.trim())}
              className="border border-black bg-black px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300 disabled:border-gray-300"
            >
              Scarica Excel
            </button>
          </div>
        </div>
      </Modal>
      {/* Weborder Export Modal */}
      {isWeborderExportModalOpen && currentSelectedRunId && (
        <WeborderExportModal
          isOpen={isWeborderExportModalOpen}
          onClose={() => setWeborderExportModalOpen(false)}
          threadId={thread.threadId || ''}
          messageId={thread.threadData?.local_memory?.thread_state?.["current-message"] as string || ''}
          optimizationId={currentSelectedRunId.toString()}
        />
      )}
    </div>
  );
}
